import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface OrderPlacement {
  id: number;
  cell_id: number;
  cell_code: string;
  order_id?: number;
  warehouse_id?: number;
}

@Injectable({ providedIn: 'root' })
export class OrderStagingService {
  constructor(private readonly http: HttpClient) {}

  order(orderId: number) {
    return firstValueFrom(
      this.http.get<any>(`${environment.BACKEND}/order-staging/orders/${orderId}`)
    );
  }

  cells(warehouseId: number, orderId: number, query = '') {
    return firstValueFrom(
      this.http.get<any[]>(`${environment.BACKEND}/order-staging/cells`, {
        params: { warehouseId, orderId, query }
      })
    );
  }

  handlings(warehouseId: number) {
    return firstValueFrom(
      this.http.get<any[]>(`${environment.BACKEND}/order-staging/handlings`, {
        params: { warehouseId }
      })
    );
  }

  pickTasks(warehouseId: number) {
    return firstValueFrom(
      this.http.get<any>(`${environment.BACKEND}/mobile/warehouse/v1/pick-tasks`, {
        params: { warehouseId }
      })
    );
  }

  taskByOrder(barcode: string) {
    return firstValueFrom(
      this.http.get<any>(
        `${environment.BACKEND}/mobile/warehouse/v1/pick-tasks/by-order/${encodeURIComponent(
          barcode
        )}`
      )
    );
  }

  joinTask(id: number) {
    return firstValueFrom(
      this.http.post<any>(`${environment.BACKEND}/mobile/warehouse/v1/pick-tasks/${id}/join`, {})
    );
  }

  assign(orderId: number, cellId: number) {
    return this.command(`order-${orderId}-assign`, `/order-staging/orders/${orderId}/cells`, {
      cellId
    });
  }

  print(orderId: number, occupancyId: number, medium: 'LABEL' | 'RECEIPT') {
    return firstValueFrom(
      this.http.post<any>(`${environment.BACKEND}/label-print/order-placement`, {
        orderId,
        occupancyId,
        medium,
        copies: 1
      })
    );
  }

  /** Store intent before sending so an uncertain response can be retried with the same UUID. */
  async command(
    scope: string,
    path: string,
    body: any,
    method: 'POST' | 'PUT' = 'POST'
  ): Promise<any> {
    const key = `autof-physical-operation:${scope}`;
    const serialized = JSON.stringify({ path, body, method });
    let pending: { intent: string; operationId: string } | undefined;
    try {
      pending = JSON.parse(localStorage.getItem(key) || 'null') || undefined;
    } catch (_) {}
    if (pending && pending.intent !== serialized) {
      throw new Error(
        'Предыдущая операция ожидает подтверждения. Повторите её перед следующим действием.'
      );
    }
    if (!pending) {
      pending = { intent: serialized, operationId: this.uuid() };
      localStorage.setItem(key, JSON.stringify(pending));
    }
    try {
      const payload = { ...body, operationId: pending.operationId };
      const result = await firstValueFrom(
        method === 'PUT'
          ? this.http.put<any>(`${environment.BACKEND}${path}`, payload)
          : this.http.post<any>(`${environment.BACKEND}${path}`, payload)
      );
      localStorage.removeItem(key);
      return result;
    } catch (error) {
      const status = Number((error as any)?.status);
      if (status >= 400 && status < 500 && ![401, 403, 408].includes(status)) {
        localStorage.removeItem(key);
      }
      throw error;
    }
  }

  retry(scope: string): Promise<any> | undefined {
    const pending = JSON.parse(localStorage.getItem(`autof-physical-operation:${scope}`) || 'null');
    if (!pending) return undefined;
    const intent = JSON.parse(pending.intent);
    return this.command(scope, intent.path, intent.body, intent.method);
  }

  hasPending(scope: string): boolean {
    return Boolean(localStorage.getItem(`autof-physical-operation:${scope}`));
  }

  pendingIntent(scope: string): any {
    try {
      return JSON.parse(
        JSON.parse(localStorage.getItem(`autof-physical-operation:${scope}`) || 'null')?.intent ||
          'null'
      );
    } catch (_) {
      return null;
    }
  }

  private uuid(): string {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(
      16,
      20
    )}-${value.slice(20)}`;
  }
}
